import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { WorkflowPermissionsPanel } from '../components/WorkflowPermissionsPanel';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/Tabs';
import { Button } from '../components/Button';
import { ImportN8nWorkflowDialog } from '../components/ImportN8nWorkflowDialog';
import { WorkflowGenerateDialog } from '../components/WorkflowGenerateDialog';
import { WorkflowBuilder } from '../components/WorkflowBuilder';
import {
  useWorkflows,
  useCreateWorkflow,
  useUpdateWorkflow,
  Workflow as ApiWorkflow,
} from '../helpers/useWorkflowsApi';
import { useCreateTaskingBotTask } from '../helpers/useTaskingBotTasks';
import { SessionNavToolbar } from '../components/SessionNavToolbar';
import type { WorkflowDefinition, WorkflowNode, WorkflowConnection } from '../helpers/workflowTypes';
import { convertN8nWorkflow } from '../helpers/n8nWorkflowConverter';
import {
  Plus,
  FileJson,
  Loader2,
  Workflow as WorkflowIcon,
  Sparkles,
  Bot,
  Clock,
  GitBranch,
  Link2,
  CheckSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../helpers/useAuth';
import { OnboardingTutorial, useShowSectionOnboarding } from '../components/OnboardingTutorial';

const workflowPageShell: React.CSSProperties = {
  padding: '8px 20px',
  minHeight: '100%',
  background:
    'linear-gradient(180deg, color-mix(in srgb, var(--background) 92%, var(--primary) 8%) 0%, var(--background) 100%)',
  color: 'var(--foreground)',
};

const workflowSurface: React.CSSProperties = {
  background: 'color-mix(in srgb, var(--card) 92%, var(--background) 8%)',
  border: '1px solid color-mix(in srgb, var(--border) 82%, transparent)',
  boxShadow: '0 10px 30px color-mix(in srgb, var(--foreground) 8%, transparent)',
};

const workflowInsetSurface: React.CSSProperties = {
  background: 'color-mix(in srgb, var(--background) 78%, var(--foreground) 3%)',
  border: '1px solid color-mix(in srgb, var(--border) 78%, transparent)',
};

const TASKING_TO_N8N_TYPE: Record<WorkflowNode['type'], string> = {
  trigger_webhook: 'n8n-nodes-base.webhook',
  trigger_schedule: 'n8n-nodes-base.scheduleTrigger',
  trigger_manual: 'n8n-nodes-base.manualTrigger',
  action_http: 'n8n-nodes-base.httpRequest',
  action_code: 'n8n-nodes-base.code',
  action_email: 'n8n-nodes-base.emailSend',
  action_slack: 'n8n-nodes-base.slack',
  action_discord: 'n8n-nodes-base.discord',
  action_ai: 'n8n-nodes-base.openAi',
  action_database: 'n8n-nodes-base.postgres',
  logic_if: 'n8n-nodes-base.if',
  logic_switch: 'n8n-nodes-base.switch',
  logic_loop: 'n8n-nodes-base.splitInBatches',
  logic_delay: 'n8n-nodes-base.wait',
  logic_parallel: 'n8n-nodes-base.merge',
};

function parseJsonValue<T>(value: T | string | null | undefined, fallback: T): T {
  if (value == null) return fallback;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function convertLegacyNodeRecord(
  apiWorkflow: ApiWorkflow,
  nodesRecord: Record<string, any>,
): WorkflowDefinition {
  const nodes: WorkflowNode[] = Object.values(nodesRecord).map((node: any) => ({
    id: String(node.id),
    type:
      node.kind === 'webhook'
        ? 'trigger_webhook'
        : node.kind === 'schedule'
          ? 'trigger_schedule'
          : node.kind === 'manual'
            ? 'trigger_manual'
            : node.kind === 'if'
              ? 'logic_if'
              : node.kind === 'switch'
                ? 'logic_switch'
                : node.kind === 'loop'
                  ? 'logic_loop'
                  : node.kind === 'delay'
                    ? 'logic_delay'
                    : node.kind === 'parallel'
                      ? 'logic_parallel'
                      : node.kind === 'database'
                        ? 'action_database'
                        : node.kind === 'code'
                          ? 'action_code'
                          : node.kind === 'email'
                            ? 'action_email'
                            : node.kind === 'slack'
                              ? 'action_slack'
                              : node.kind === 'discord'
                                ? 'action_discord'
                                : node.kind === 'ai' || node.type === 'ai'
                                  ? 'action_ai'
                                  : 'action_http',
    name: String(node.kind || node.type || 'Node'),
    position: { x: Number(node.x || 0), y: Number(node.y || 0) },
    config: typeof node.config === 'object' && node.config ? node.config : {},
  }));

  const connections: WorkflowConnection[] = [];
  for (const node of Object.values(nodesRecord) as any[]) {
    for (const outputId of Array.isArray(node.outputs) ? node.outputs : []) {
      connections.push({
        id: crypto.randomUUID(),
        sourceNodeId: String(node.id),
        sourceHandle: 'output',
        targetNodeId: String(outputId),
        targetHandle: 'input',
      });
    }
  }

  return {
    id: apiWorkflow.id,
    name: apiWorkflow.name,
    description: apiWorkflow.description || undefined,
    nodes,
    connections,
    variables: {},
    createdAt: apiWorkflow.createdAt,
    updatedAt: apiWorkflow.updatedAt,
  };
}

function convertApiWorkflowToBuilder(apiWorkflow: ApiWorkflow): WorkflowDefinition {
  const parsedNodes = parseJsonValue<any>(apiWorkflow.nodes, []);
  const parsedConnections = parseJsonValue<any>(apiWorkflow.connections, {});

  if (Array.isArray(parsedNodes)) {
    return {
      ...convertN8nWorkflow({
        name: apiWorkflow.name,
        description: apiWorkflow.description || undefined,
        nodes: parsedNodes,
        connections:
          parsedConnections && typeof parsedConnections === 'object' ? parsedConnections : {},
      }),
      id: apiWorkflow.id,
      name: apiWorkflow.name,
      description: apiWorkflow.description || undefined,
      createdAt: apiWorkflow.createdAt,
      updatedAt: apiWorkflow.updatedAt,
    };
  }

  if (parsedNodes && typeof parsedNodes === 'object') {
    return convertLegacyNodeRecord(apiWorkflow, parsedNodes);
  }

  return {
    id: apiWorkflow.id,
    name: apiWorkflow.name,
    description: apiWorkflow.description || undefined,
    nodes: [],
    connections: [],
    variables: {},
    createdAt: apiWorkflow.createdAt,
    updatedAt: apiWorkflow.updatedAt,
  };
}

function buildN8nParameters(node: WorkflowNode): Record<string, any> {
  const config = node.config || {};

  switch (node.type) {
    case 'trigger_webhook':
      return {
        path: config.webhookPath || 'workflow-trigger',
        httpMethod: 'POST',
      };
    case 'trigger_schedule':
      return {
        rule: { cronExpression: config.cronExpression || '0 * * * *' },
      };
    case 'action_http':
      return {
        method: config.method || 'GET',
        url: config.url || '',
        headers: config.headers || {},
        body: config.body || '',
      };
    case 'action_code':
      return {
        language: config.language || 'javascript',
        code: config.code || '',
      };
    case 'action_email':
      return {
        to: config.to || '',
        subject: config.subject || '',
        message: config.bodyTemplate || '',
      };
    case 'action_slack':
    case 'action_discord':
      return {
        webhookUrl: config.webhookUrl || '',
        text: config.message || '',
      };
    case 'action_ai':
      return {
        prompt: config.prompt || '',
        model: config.model || 'qwen3-coder-next:cloud',
      };
    case 'action_database':
      return {
        operation: config.operation || 'query',
        table: config.table || '',
        query: config.query || '',
      };
    case 'logic_if':
      return {
        condition: config.condition || 'true',
      };
    case 'logic_switch':
      return {
        value1: config.condition || 'input.value',
        rules: Array.isArray(config.cases)
          ? config.cases.map((item: any) => ({
              value: item?.value || '',
              outputKey: item?.label || '',
            }))
          : [],
      };
    case 'logic_loop':
      return {
        batchSize: config.loopCount || 1,
        loopVariable: config.loopVariable || 'item',
      };
    case 'logic_delay':
      return {
        amount: config.delayMs ? Math.max(1, Math.round(Number(config.delayMs) / 1000)) : 1,
      };
    case 'logic_parallel':
      return {
        mode: 'append',
      };
    default:
      return {};
  }
}

function serializeBuilderWorkflow(workflow: WorkflowDefinition) {
  const nodeNameById = new Map<string, string>();
  const nodes = workflow.nodes.map((node, index) => {
    const uniqueName = node.name?.trim() || `Node ${index + 1}`;
    nodeNameById.set(node.id, uniqueName);

    return {
      id: node.id,
      name: uniqueName,
      type: TASKING_TO_N8N_TYPE[node.type] || 'n8n-nodes-base.httpRequest',
      position: [Math.round(node.position.x), Math.round(node.position.y)] as [number, number],
      parameters: buildN8nParameters(node),
    };
  });

  const connections: Record<string, Record<string, Array<Array<{ node: string; type: string; index: number }>>>> =
    {};

  for (const connection of workflow.connections) {
    const sourceName = nodeNameById.get(connection.sourceNodeId);
    const targetName = nodeNameById.get(connection.targetNodeId);
    if (!sourceName || !targetName) continue;

    if (!connections[sourceName]) connections[sourceName] = {};
    if (!connections[sourceName].main) connections[sourceName].main = [];
    if (!connections[sourceName].main[0]) connections[sourceName].main[0] = [];

    connections[sourceName].main[0].push({
      node: targetName,
      type: 'main',
      index: 0,
    });
  }

  return { nodes, connections };
}

function deriveWorkflowSubtasks(workflow: WorkflowDefinition): string[] {
  return workflow.nodes
    .filter((node) => !node.type.startsWith('trigger_'))
    .map((node) => node.name.trim())
    .filter((title, index, list) => title.length > 0 && list.indexOf(title) === index);
}

const WorkflowsPage: React.FC = () => {
  const { authState } = useAuth();
  const currentUserId = authState.type === 'authenticated' ? authState.user?.id : null;
  const showTutorial = useShowSectionOnboarding('workflows', currentUserId);
  const [tutorialDismissed, setTutorialDismissed] = useState(false);
  const { data: workflowsData, isLoading, error, refetch } = useWorkflows();
  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();
  const createTask = useCreateTaskingBotTask();

  const workflows = workflowsData?.workflows || [];
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [mainTab, setMainTab] = useState('workflows');
  const [participants] = useState<any[]>([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [addNodesDialogOpen, setAddNodesDialogOpen] = useState(false);
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);

  useEffect(() => {
    if (workflows.length > 0 && !selectedWorkflowId) {
      setSelectedWorkflowId(workflows[0].id);
    }
  }, [workflows, selectedWorkflowId]);

  useEffect(() => {
    if (pendingSelectId && workflows.some((workflow) => workflow.id === pendingSelectId)) {
      setSelectedWorkflowId(pendingSelectId);
      setPendingSelectId(null);
      setStatusFilter('all');
      setMainTab('workflows');
    }
  }, [pendingSelectId, workflows]);

  const filteredWorkflows = useMemo(() => {
    return workflows.filter((workflow) => {
      if (statusFilter === 'all') return true;
      const derivedStatus = workflow.active ? 'active' : 'draft';
      return derivedStatus === statusFilter;
    });
  }, [statusFilter, workflows]);

  const selectedWorkflow = useMemo(
    () =>
      workflows.find((workflow) => workflow.id === selectedWorkflowId) ||
      filteredWorkflows[0] ||
      null,
    [filteredWorkflows, selectedWorkflowId, workflows],
  );

  const selectedWorkflowBuilder = useMemo(
    () => (selectedWorkflow ? convertApiWorkflowToBuilder(selectedWorkflow) : null),
    [selectedWorkflow],
  );

  const handleImportN8nWorkflow = useCallback(
    async (imported: WorkflowDefinition) => {
      const serialized = serializeBuilderWorkflow(imported);

      try {
        const result = await createWorkflow.mutateAsync({
          name: imported.name,
          description: imported.description,
          nodes: serialized.nodes,
          connections: serialized.connections,
          active: false,
        });

        setPendingSelectId(result.workflow.id);
        setStatusFilter('all');
      } catch (err) {
        console.error('Failed to import workflow:', err);
        toast.error('Failed to import workflow');
      }
    },
    [createWorkflow],
  );

  const handleCreateBlankWorkflow = useCallback(async () => {
    const result = await createWorkflow.mutateAsync({
      name: 'New Workflow',
      description: 'Blank workflow created from the workflows builder.',
      nodes: [
        {
          name: 'Manual Trigger',
          type: 'n8n-nodes-base.manualTrigger',
          position: [220, 220],
          parameters: {},
        },
      ],
      connections: {},
      active: false,
    });

    setPendingSelectId(result.workflow.id);
    toast.success('Blank workflow created');
  }, [createWorkflow]);

  const handleBuilderSave = useCallback(
    async (workflow: WorkflowDefinition) => {
      if (!selectedWorkflow) return;

      const serialized = serializeBuilderWorkflow(workflow);
      await updateWorkflow.mutateAsync({
        id: selectedWorkflow.id,
        name: workflow.name,
        description: workflow.description,
        nodes: JSON.stringify(serialized.nodes),
        connections: JSON.stringify(serialized.connections),
      });

      toast.success('Workflow saved');
      refetch();
    },
    [refetch, selectedWorkflow, updateWorkflow],
  );

  const handleCreateTaskPlan = useCallback(async () => {
    if (!selectedWorkflow || !selectedWorkflowBuilder) return;

    const subtasks = deriveWorkflowSubtasks(selectedWorkflowBuilder);
    await createTask.mutateAsync({
      title: selectedWorkflow.name,
      description:
        selectedWorkflow.description ||
        'Task plan created from workflow. Complete the subtasks in sequence or in parallel as needed.',
      workflowId: selectedWorkflow.id,
      subtasks,
    });

    toast.success('Created task and subtasks from workflow');
  }, [createTask, selectedWorkflow, selectedWorkflowBuilder]);

  const handleSaveAsDraft = async () => {
    if (!selectedWorkflow) return;
    await updateWorkflow.mutateAsync({ id: selectedWorkflow.id, active: false });
    toast.success('Workflow saved as draft');
    refetch();
  };

  const handlePublish = async () => {
    if (!selectedWorkflow) return;
    await updateWorkflow.mutateAsync({ id: selectedWorkflow.id, active: true });
    toast.success('Workflow published');
    refetch();
  };

  const handleUpdatePermission = (participantId: string, permissionLevel: string) => {
    console.log('Update permission:', participantId, permissionLevel);
  };

  const handleRemoveParticipant = (participantId: string) => {
    console.log('Remove participant:', participantId);
  };

  const handleInviteParticipant = () => {
    console.log('Invite participant');
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...workflowPageShell,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Loader2 size={20} className="animate-spin" />
        <span>Loading workflows...</span>
      </div>
    );
  }

  const showEmptyState = error || workflows.length === 0;

  if (showEmptyState) {
    return (
      <div style={workflowPageShell}>
        <div
          style={{
            ...workflowSurface,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center',
            borderRadius: '12px',
          }}
        >
          <div
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background:
                'linear-gradient(135deg, var(--primary) 0%, var(--primary-foreground, #6366f1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
              opacity: 0.15,
            }}
          >
            <WorkflowIcon size={56} style={{ color: 'var(--foreground)' }} />
          </div>

          <h2
            style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--foreground)',
              marginBottom: '8px',
            }}
          >
            No Workflows Saved
          </h2>
          <p
            style={{
              fontSize: '0.9375rem',
              color: 'var(--muted-foreground)',
              maxWidth: '420px',
              marginBottom: '12px',
              lineHeight: 1.5,
            }}
          >
            Create workflows with AI, edit each node configuration, and turn them into a task plan
            when you are ready to execute the work.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '32px',
              maxWidth: '420px',
              width: '100%',
            }}
          >
            {[
              { icon: <Clock size={18} />, label: 'Schedule tasks' },
              { icon: <Bot size={18} />, label: 'AI processing' },
              { icon: <Link2 size={18} />, label: 'API integrations' },
              { icon: <GitBranch size={18} />, label: 'Conditional logic' },
            ].map((feature) => (
              <div
                key={feature.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  ...workflowInsetSurface,
                  fontSize: '0.8125rem',
                  color: 'var(--foreground)',
                }}
              >
                <span style={{ color: 'var(--primary)' }}>{feature.icon}</span>
                {feature.label}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button size="lg" onClick={() => setGenerateDialogOpen(true)}>
              <Sparkles size={18} />
              Create from AI
            </Button>
            <Button size="lg" variant="outline" onClick={() => setImportDialogOpen(true)}>
              <FileJson size={18} />
              Import n8n Workflow
            </Button>
          </div>
        </div>

        <WorkflowGenerateDialog
          open={generateDialogOpen}
          onOpenChange={setGenerateDialogOpen}
          onSuccess={(workflowId) => {
            setGenerateDialogOpen(false);
            setPendingSelectId(workflowId);
          }}
        />
        <ImportN8nWorkflowDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          onImport={handleImportN8nWorkflow}
        />
      </div>
    );
  }

  return (
    <div style={workflowPageShell}>
      {showTutorial && !tutorialDismissed && (
        <OnboardingTutorial
          type="workflows"
          userId={currentUserId}
          onComplete={() => setTutorialDismissed(true)}
        />
      )}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
        </TabsList>
        <TabsContent value="workflows">
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList>
              <TabsTrigger value="all">All ({workflows.length})</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="draft">Draft</TabsTrigger>
              <TabsTrigger value="archived">Archived</TabsTrigger>
            </TabsList>
            <TabsContent value={statusFilter} style={{ paddingTop: '20px' }}>
              <div
                style={{
                  ...workflowSurface,
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexWrap: 'wrap',
                  padding: '14px',
                  borderRadius: '12px',
                }}
              >
                <select
                  value={selectedWorkflow?.id || ''}
                  onChange={(e) => setSelectedWorkflowId(e.target.value || null)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid color-mix(in srgb, var(--border) 78%, transparent)',
                    minWidth: '220px',
                    background:
                      'color-mix(in srgb, var(--background) 72%, var(--card) 28%)',
                    color: 'var(--foreground)',
                    fontSize: '14px',
                    boxShadow:
                      'inset 0 1px 0 color-mix(in srgb, white 35%, transparent)',
                  }}
                >
                  <option value="">Select a workflow...</option>
                  {filteredWorkflows.length === 0 ? (
                    <option value="" disabled>
                      No workflows found
                    </option>
                  ) : (
                    filteredWorkflows.map((workflow) => (
                      <option key={workflow.id} value={workflow.id}>
                        {workflow.name} ({workflow.active ? 'active' : 'draft'})
                      </option>
                    ))
                  )}
                </select>
                <Button variant="outline" size="sm" onClick={() => setGenerateDialogOpen(true)}>
                  <Sparkles size={16} />
                  AI Generate
                </Button>
                <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)}>
                  <FileJson size={16} />
                  Import n8n
                </Button>
                {selectedWorkflowBuilder && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAddNodesDialogOpen(true)}
                    >
                      <Sparkles size={16} />
                      AI Add Nodes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCreateTaskPlan}
                      disabled={createTask.isPending}
                    >
                      <CheckSquare size={16} />
                      Create Task Plan
                    </Button>
                  </>
                )}
                <Button variant="outline" size="sm" onClick={handleCreateBlankWorkflow}>
                  <Plus size={16} />
                  New Workflow
                </Button>
                {selectedWorkflow && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSaveAsDraft}
                      disabled={updateWorkflow.isPending}
                    >
                      Save as Draft
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handlePublish}
                      disabled={updateWorkflow.isPending}
                    >
                      Publish
                    </Button>
                  </>
                )}
              </div>
              {selectedWorkflowBuilder ? (
                <WorkflowBuilder
                  key={selectedWorkflow?.id || 'workflow-builder'}
                  workflow={selectedWorkflowBuilder}
                  onSave={handleBuilderSave}
                />
              ) : (
                <div
                  style={{
                    ...workflowInsetSurface,
                    padding: '40px',
                    textAlign: 'center',
                    color: 'var(--muted-foreground)',
                    border: '1px dashed color-mix(in srgb, var(--border) 82%, transparent)',
                    borderRadius: '8px',
                  }}
                >
                  <p>Select a workflow from the dropdown above to view and edit it.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="permissions">
          {selectedWorkflow && (
            <WorkflowPermissionsPanel
              workflowId={selectedWorkflow.id}
              participants={participants}
              onUpdatePermission={handleUpdatePermission}
              onRemoveParticipant={handleRemoveParticipant}
              onInviteParticipant={handleInviteParticipant}
            />
          )}
        </TabsContent>
      </Tabs>

      <WorkflowGenerateDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        onSuccess={(workflowId) => {
          setGenerateDialogOpen(false);
          setPendingSelectId(workflowId);
        }}
      />
      <ImportN8nWorkflowDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImport={handleImportN8nWorkflow}
      />
      {selectedWorkflowBuilder && (
        <WorkflowGenerateDialog
          open={addNodesDialogOpen}
          onOpenChange={setAddNodesDialogOpen}
          existingWorkflow={{
            id: selectedWorkflow!.id,
            name: selectedWorkflow!.name,
            nodes: selectedWorkflowBuilder.nodes.map((n) => ({
              name: n.name,
              type: n.type,
            })),
          }}
          onSuccess={(workflowId) => {
            setAddNodesDialogOpen(false);
            setPendingSelectId(workflowId);
          }}
        />
      )}
    </div>
  );
};

function WorkflowsPageWithToolbar() {
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        <WorkflowsPage />
      </div>
      <SessionNavToolbar side="right" />
    </div>
  );
}

export default WorkflowsPageWithToolbar;
